// Curated catalog of LLM providers and a tiered ranking of their
// models. Backed by pi-ai's built-in catalog (`getBuiltinModel` /
// `getBuiltinModels`) — we just filter to providers that work directly
// from the browser (CORS-friendly, no SigV4 / OAuth-only) and
// promote known strong tool-callers to the top of each list.

// pi-ai 0.80.0 made the package root core-only: the static catalog reads
// `getModel`/`getModels` moved off it to `getBuiltinModel`/`getBuiltinModels`
// under `/providers/all` (the deprecated `/compat` shim re-exports the old
// names, but the README says to avoid it in bundled apps). They're the same
// functions, so we alias on import and leave the call sites untouched.
// `clampThinkingLevel` is unaffected and stays on the root entrypoint.
import { clampThinkingLevel } from "@earendil-works/pi-ai";
import { getBuiltinModel as getModel, getBuiltinModels as getModels } from "@earendil-works/pi-ai/providers/all";

// Browser-callable providers, in the order we want to show them.
// Each entry has the pi-ai provider id, a friendly label, and the
// canonical base URL we'll store alongside saved credentials.
//
// We intentionally exclude OAuth/SigV4-only providers (amazon-bedrock,
// google-vertex, github-copilot, openai-codex), Azure (customer-config
// dependent), and regional/internal providers (xiaomi-*, moonshot-cn,
// minimax-cn). For everything else there's a "custom" entry that
// drops down to the synthesized openai-completions path.
export const CURATED_PROVIDERS = [
    { id: "openrouter",        label: "OpenRouter",  baseUrl: "https://openrouter.ai/api/v1" },
    { id: "anthropic",         label: "Anthropic",   baseUrl: "https://api.anthropic.com" },
    { id: "openai",            label: "OpenAI",      baseUrl: "https://api.openai.com/v1" },
    { id: "google",            label: "Google",      baseUrl: "https://generativelanguage.googleapis.com" },
    { id: "xai",               label: "xAI",         baseUrl: "https://api.x.ai/v1" },
    { id: "groq",              label: "Groq",        baseUrl: "https://api.groq.com/openai/v1" },
    { id: "cerebras",          label: "Cerebras",    baseUrl: "https://api.cerebras.ai/v1" },
    { id: "deepseek",          label: "DeepSeek",    baseUrl: "https://api.deepseek.com/v1" },
    { id: "mistral",           label: "Mistral",     baseUrl: "https://api.mistral.ai/v1" },
    { id: "fireworks",         label: "Fireworks",   baseUrl: "https://api.fireworks.ai/inference/v1" },
    // BYO-gateway. No fixed baseUrl — the tenant supplies it; the picker shows
    // free-text URL + model fields and the chat agent synthesizes an
    // openai-completions Model against it. The provider id is the bare string
    // "custom" everywhere (picker, chatCreds, piChat) — there is no separate
    // sentinel constant to keep in sync.
    { id: "custom",            label: "Custom (OpenAI-compatible)", baseUrl: "" },
];

// Picker groups. The catalog carries no capability tier, and an id-pattern
// list used to stand in for one: any new or variant id the patterns did not
// know sank to the bottom and, until recently, also had its reasoning turned
// off by default. Groups are now read straight from the catalog entry:
// `reasoning` models first, everything else after. Within a group the sort
// is context window, then output price, then name; cost and context are
// shown per row so the user weighs them directly.
export const MODEL_TIERS = {
    REASONING: "reasoning",
    OTHER: "other",
};

// Drop these from the picker entirely — they're either deprecated,
// audio/vision-only, or known weak at multi-step tool use.
const EXCLUDE_PATTERNS = [
    /^claude-3-(haiku|sonnet|opus)/,    // gen-3 base; outclassed by 3.5+
    /^claude-3-5-(sonnet|haiku)-20240/,  // earliest 3.5 snapshots
    /^gpt-4($|-)/,                       // pre-4.1
    /^gpt-4o-2024-05-13$/,
    /-preview-(0[1-6]-|0[1-6]2)/,        // dated previews older than current
    /^gemini-1\.5/,
    /^gemini-live/,
    /^gemma-/,                           // OSS, no native tool calling
    /^open-(mistral|mixtral)/,
    /^ministral-/,
    /^mistral-nemo/,
    /^pixtral-/,
    /^codestral/,
    /^devstral/,
    /^magistral/,
    /^grok-(beta|vision-beta|2)/,
    /^grok-3(-fast|-mini)?(-latest)?$/,  // superseded by Grok 4
    /^llama3-/,                          // 8192 ctx
    /^gemma2-/,
    /^moonshotai\/kimi-k2-instruct$/,    // older Kimi
];

function groupFor(model) {
    return model?.reasoning ? MODEL_TIERS.REASONING : MODEL_TIERS.OTHER;
}

function isExcluded(model) {
    // Router pseudo-models (OpenRouter "auto") carry a negative price
    // sentinel: they pick a real model per request, so the picker cannot
    // show a cost or a fixed capability for them.
    const cost = model?.cost;
    if (cost && (Number(cost.input) < 0 || Number(cost.output) < 0)) return true;
    const lower = String(model?.id || "").toLowerCase();
    return EXCLUDE_PATTERNS.some((re) => re.test(lower));
}

const GROUP_ORDER = { reasoning: 0, other: 1 };

// Ranked model list for a curated provider. Reasoning-capable models first,
// then context window descending, then output price descending as a rough
// capability proxy, then name. `isDefault` marks the provider's configured
// default so the picker can star it without an id list.
export function getRankedModels(provider) {
    let raw;
    try {
        raw = getModels(provider);
    } catch {
        return [];
    }
    const defaultId = PROVIDER_DEFAULTS[provider] || null;
    const filtered = (raw || []).filter((m) => !isExcluded(m));
    const decorated = filtered.map((m) => ({ model: m, tier: groupFor(m), isDefault: m.id === defaultId }));
    decorated.sort((a, b) => {
        const t = GROUP_ORDER[a.tier] - GROUP_ORDER[b.tier];
        if (t !== 0) return t;
        if ((b.model.contextWindow || 0) !== (a.model.contextWindow || 0)) {
            return (b.model.contextWindow || 0) - (a.model.contextWindow || 0);
        }
        const price = (b.model.cost?.output || 0) - (a.model.cost?.output || 0);
        if (price !== 0) return price;
        return String(a.model.name || a.model.id).localeCompare(String(b.model.name || b.model.id));
    });
    return decorated;
}

// Default model per provider: exact catalog ids, validated against the
// catalog at use (defaultModelFor falls back to the ranked list when an
// id is absent). This is the only per-model curation left in this file.
const PROVIDER_DEFAULTS = {
    anthropic: "claude-sonnet-4-6",
    openai: "gpt-5.5",
    google: "gemini-3.1-pro-preview",
    openrouter: "anthropic/claude-sonnet-4.6",
    xai: "grok-4.3",
    groq: "llama-3.3-70b-versatile",
    cerebras: "gpt-oss-120b",
    deepseek: "deepseek-v4-pro",
    mistral: "mistral-medium-latest",
    fireworks: "accounts/fireworks/models/deepseek-v4-pro",
};

// Optional second model used by sub-agents (the kernel-save extractor,
// the suggest_kernel judge, etc). Defaults pick a cheap/fast model
// from each provider so non-flagship reasoning doesn't burn the
// primary's rate. Falls back to the primary when no cheap pairing
// makes sense for that provider.
const TASK_MODEL_DEFAULTS = {
    anthropic: "claude-haiku-4-5",
    openai: "gpt-5.4-mini",
    openrouter: "anthropic/claude-haiku-4.5",
};

export function defaultModelFor(provider) {
    const preferred = PROVIDER_DEFAULTS[provider];
    if (preferred) {
        const m = getModel(provider, preferred);
        if (m) return preferred;
    }
    // Fall back to the top of the ranked list.
    const ranked = getRankedModels(provider);
    return ranked[0]?.model.id || "";
}

// Same shape as `defaultModelFor` but for the task / sub-agent role.
// Returns the primary default when the provider has no cheaper
// pairing — callers can treat "primary == task" as "no separate task
// model needed."
export function defaultTaskModelFor(provider) {
    const preferred = TASK_MODEL_DEFAULTS[provider];
    if (preferred) {
        const m = getModel(provider, preferred);
        if (m) return preferred;
    }
    return defaultModelFor(provider);
}

// Some models *mandate* a thinking level — they reject an explicit
// "no thinking" request and 400 the whole turn if handed one (Fable:
// `thinking: {type: "disabled"}` / `reasoning: {effort: "none"}`; Gemini 3:
// "Reasoning is mandatory for this endpoint and cannot be disabled").
// pi-ai's catalog encodes this: `thinkingLevelMap.off: null` marks "off"
// unsupported (getSupportedThinkingLevels filters it out and every provider
// path omits the disable param), a non-null mapping marks it explicitly
// supported, and an absent key means the entry doesn't say. Read that
// tri-state — null = no answer — instead of duplicating the model list here.
// See O-1994 / O-2011 / O-2014 for the Fable history behind the guard.
function entryMandatesThinking(m) {
    if (!m) return null;
    if (!m.reasoning) return false;
    const map = m.thinkingLevelMap;
    if (!map || !Object.hasOwn(map, "off")) return null;
    return map.off === null;
}

// Gateway providers list vendor-prefixed ids ("google/gemini-3.6-flash",
// optionally with a ":variant" suffix). Their generated entries can lag the
// vendor's native ones — the openrouter Gemini 3 mirrors carry no
// `thinkingLevelMap` at all while google's carry `{off: null}` — so when the
// gateway entry doesn't answer, ask the vendor's native entry for the same
// model.
function vendorEntryFor(modelId) {
    const id = stripModelVariantTag(modelId);
    const slash = id.indexOf("/");
    if (slash <= 0) return null;
    const vendor = id.slice(0, slash).replace(/^~/, "");
    const rest = id.slice(slash + 1);
    const bare = rest.replace(/:[^/]*$/, "");
    return lookupCatalogModel(vendor, rest) || (bare !== rest ? lookupCatalogModel(vendor, bare) : null);
}

// Safety net for ids no catalog entry answers for — Fable aliases pi-ai
// doesn't list (a bare `claude-fable-latest`, the `~anthropic/…` floating
// alias). Anchored to a path/string boundary so a model that merely contains
// the substring can't false-match. Add a pattern here only when the id is
// genuinely absent from pi-ai's catalog; cataloged models belong upstream in
// `thinkingLevelMap`.
const MANDATORY_THINKING_PATTERNS = [
    /(^|\/)claude-fable-/,
];

// True when the model rejects a disabled-thinking request and therefore
// must always carry a thinking level. Catalog first (own entry, then the
// vendor entry behind a gateway id), id patterns as the last resort.
export function modelMandatesThinking(provider, modelId) {
    if (!modelId) return false;
    const own = entryMandatesThinking(lookupCatalogModel(provider, modelId));
    if (own !== null) return own;
    const vendor = entryMandatesThinking(vendorEntryFor(modelId));
    if (vendor !== null) return vendor;
    const lower = String(modelId).toLowerCase();
    return MANDATORY_THINKING_PATTERNS.some((re) => re.test(lower));
}

// Default thinking depth for a freshly-created chat on this model.
// Every reasoning-capable model defaults to "high": the chat's value is
// multi-step investigation, and a reasoning model with its reasoning
// disabled gives up early and concludes on weak evidence. The tier
// patterns still order the picker, but they no longer gate the default,
// because a vendor suffix the patterns do not know (a dated or variant
// id) used to silently turn reasoning off. Non-reasoning and unknown
// models stay "off". The result is clamped to what the model actually
// supports (getSupportedThinkingLevels) so we never hand the agent a
// level it can't honour. Callers treat a chat's null/unset thinkingLevel
// as "use this default"; an explicit choice (incl. "off") always wins.
export const DEFAULT_REASONING_LEVEL = "high";

export function defaultThinkingLevelFor(provider, modelId) {
    const m = lookupCatalogModel(provider, modelId);
    // Mandatory-thinking models can't be handed "off" — force a real level
    // even when the id is absent from the catalog (clamp when we can).
    if (modelMandatesThinking(provider, modelId)) {
        return m ? clampThinkingLevel(m, DEFAULT_REASONING_LEVEL) : DEFAULT_REASONING_LEVEL;
    }
    if (!m?.reasoning) return "off";
    return clampThinkingLevel(m, DEFAULT_REASONING_LEVEL);
}

// The thinking level a chat should actually run at, given its persisted
// choice and current model. An explicit persisted choice wins over the
// model default — EXCEPT "off" on a model that mandates thinking, which
// would 400 the turn (see modelMandatesThinking). There we ignore the
// stale/explicit "off" and fall back to the model default. This closes the
// gap the pill's disabled "off" can't: a chat that persisted "off" on a
// tolerant model and was later repointed to Fable. The persisted value is
// left untouched (we don't rewrite it), so switching back to a model that
// accepts "off" restores the user's choice. null/unset → the model default.
// Merge a lazily loaded chat body's persisted thinking level into live chat
// state. A level the user picked while the body was still loading wins over
// the persisted one; an unset level stays null (= model default) rather than
// becoming an explicit "off".
export function mergeLoadedThinkingLevel(liveLevel, persistedLevel) {
    return liveLevel ?? persistedLevel ?? null;
}

export function resolveThinkingLevel(provider, modelId, persistedLevel) {
    if (persistedLevel && !(persistedLevel === "off" && modelMandatesThinking(provider, modelId))) {
        return persistedLevel;
    }
    return defaultThinkingLevelFor(provider, modelId);
}

export function findCuratedProvider(id) {
    return CURATED_PROVIDERS.find((p) => p.id === id) || null;
}

// Lookup catalog Model for a saved (provider, modelId). Returns null
// if the provider/model isn't in pi-ai's registry — caller should
// fall back to the synthesized openai-completions Model.
export function lookupCatalogModel(provider, modelId) {
    if (!provider || !modelId) return null;
    try {
        return getModel(provider, modelId) || null;
    } catch {
        return null;
    }
}

// Strip a trailing variant tag the catalog doesn't list as its own entry —
// notably the 1M-context beta suffix, e.g. "claude-opus-4-8[1m]". Exported for
// the cost test. Only touches a trailing "[...]"; ids without one pass through.
export function stripModelVariantTag(modelId) {
    return String(modelId ?? "").replace(/\s*\[[^\]]*\]\s*$/, "");
}

// Per-million-token USD rates for a saved (provider, modelId), straight from
// pi-ai's bundled registry: { input, output, cacheRead, cacheWrite }. Returns
// null when the model isn't catalogued — callers should price nothing (show
// volume only) rather than guess. Same provider/model keying as
// lookupCatalogModel, so the same matching caveats apply: the id must match
// pi-ai's catalog id, and the SAME id can carry different rates under
// different providers (anthropic vs openrouter vs bedrock), so pass the
// reporting provider, don't assume one.
//
// Variant fallback: a 1M-context response-model id like "claude-opus-4-8[1m]"
// has no catalog entry of its own — the base model already carries the 1M
// context window and its rates — so we strip the "[1m]" tag and retry at the
// base rate. CAVEAT: Anthropic's 1M tier bills context above ~200K at a
// premium the flat catalog rate doesn't model, so a [1m] estimate priced this
// way is CONSERVATIVE (an under-count). Exact long-context pricing would need
// per-tier rates pi-ai doesn't carry today; revisit if the under-count matters.
export function getModelCost(provider, modelId) {
    const direct = lookupCatalogModel(provider, modelId)?.cost;
    if (direct) return direct;
    const base = stripModelVariantTag(modelId);
    if (base && base !== modelId) return lookupCatalogModel(provider, base)?.cost || null;
    return null;
}

// Group label used in the picker UI. Kept here so the chat console
// doesn't have to duplicate the mapping.
export function tierLabel(tier) {
    if (tier === MODEL_TIERS.REASONING) return "Reasoning";
    return "Other";
}
